import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  History,
  GitMerge,
  User,
  Calendar,
  FileText,
  CheckSquare,
  MessageSquare,
  Eye,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MergeHistory as MergeHistoryType } from '@/types/duplicates';
import { useMergeHistory } from '@/hooks/useDuplicates';
import { cn } from '@/lib/utils';

interface MergeHistoryProps {
  limit?: number;
}

export const MergeHistory: React.FC<MergeHistoryProps> = ({ limit = 20 }) => {
  const [page, setPage] = useState(1);
  const [selectedMerge, setSelectedMerge] = useState<MergeHistoryType | null>(null);

  const { data, isLoading, error } = useMergeHistory({ page, limit });

  const history = Array.isArray(data?.history) ? data!.history : [];
  const pagination = data?.pagination;

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <p className="text-red-600">Failed to load merge history</p>
        </div>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Merge History</h3>
          <p className="text-gray-600">No duplicates have been merged yet.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <History className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Merge History</h2>
            <p className="text-sm text-gray-600">
              {pagination?.total || history.length} total merges
            </p>
          </div>
        </div>
      </div>

      {/* History table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Merge Details
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Items Moved
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Merged By
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Date
              </th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {history.map((merge) => (
              <tr key={merge.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-4 px-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <GitMerge className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {merge.mergedLeadIds.length + 1} leads merged
                      </p>
                      <p className="text-xs text-gray-500">
                        Primary: {merge.primaryLeadId.slice(0, 8)}...
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <span className="flex items-center">
                      <FileText className="h-3 w-3 mr-1" />
                      {merge.activitiesMoved} activities
                    </span>
                    <span className="flex items-center">
                      <MessageSquare className="h-3 w-3 mr-1" />
                      {merge.notesMoved} notes
                    </span>
                    <span className="flex items-center">
                      <CheckSquare className="h-3 w-3 mr-1" />
                      {merge.tasksMoved} tasks
                    </span>
                  </div>
                </td>
                <td className="py-4 px-4">
                  {merge.mergedBy ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-3 w-3 text-blue-600" />
                      </div>
                      <span className="text-sm text-gray-900">{merge.mergedBy.name}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Unknown</span>
                  )}
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-3 w-3 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {new Date(merge.mergedAt).toLocaleDateString()}
                    </span>
                  </div>
                </td>
                <td className="py-4 px-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedMerge(merge)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.pages}
          </p>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={pagination.page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              disabled={pagination.page === pagination.pages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      <AnimatePresence>
        {selectedMerge && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedMerge(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Merge Details</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedMerge(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Primary Lead ID</p>
                  <p className="text-sm font-mono bg-gray-100 p-2 rounded">
                    {selectedMerge.primaryLeadId}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Merged Lead IDs</p>
                  <div className="space-y-1">
                    {selectedMerge.mergedLeadIds.map(id => (
                      <p key={id} className="text-sm font-mono bg-gray-100 p-2 rounded">
                        {id}
                      </p>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Field Resolutions</p>
                  <div className="bg-gray-100 p-3 rounded text-sm font-mono overflow-x-auto">
                    <pre>{JSON.stringify(selectedMerge.fieldResolutions, null, 2)}</pre>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Activities Moved</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedMerge.activitiesMoved}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Notes Moved</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedMerge.notesMoved}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tasks Moved</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedMerge.tasksMoved}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Files Moved</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedMerge.filesMoved}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Merged At</p>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedMerge.mergedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};
